import { Box, CircularProgress, circularProgressClasses, useTheme } from '@mui/material'
import { tss } from 'tss-react/mui'
import { LoadingSpinnerProps } from '../types'

export interface NavigationLoadingSpinnerProps extends Omit<
  LoadingSpinnerProps,
  'type' | 'classes'
> {
  spinnerColor?: string
  spinnerSize?: number
  background?: 'dark' | 'light'
  classes?: Partial<ReturnType<typeof useStyles>['classes']>
  fullWidth?: boolean
  fullHeight?: boolean
}

/**
 * Renders the navigation loading spinner
 */
export function NavigationLoadingSpinner({
  spinnerColor,
  fullWidth,
  fullHeight,
  spinnerSize: _spinnerSize,
  ...rest
}: NavigationLoadingSpinnerProps) {
  const theme = useTheme()
  const spinnerSize = _spinnerSize ?? 12
  const thickness = rest.thickness ?? 5
  const backgroundThickness = thickness - 0.5
  const background = rest.background ?? 'light'

  const { classes } = useStyles({
    spinnerColor: spinnerColor ?? theme.palette.common.doss.gray[10],
    spinnerSize,
    classesOverrides: rest.classes,
    background,
    fullWidth,
    fullHeight,
  })

  return (
    <Box className={classes.box}>
      <CircularProgress
        className={classes.fixed}
        size={spinnerSize}
        thickness={backgroundThickness}
        value={100}
        variant="determinate"
        {...rest}
      />
      <CircularProgress
        className={classes.root}
        disableShrink
        size={spinnerSize}
        thickness={thickness}
      />
    </Box>
  )
}

const useStyles = tss
  .withParams<{
    spinnerColor: string
    spinnerSize: number
    background: 'dark' | 'light'
    fullWidth?: boolean
    fullHeight?: boolean
  }>()
  .create(({ spinnerColor, background, fullHeight, fullWidth, theme }) => ({
    box: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: fullHeight ? '100%' : undefined,
      width: fullWidth ? '100%' : undefined,
    },
    root: {
      position: 'absolute',
      color: spinnerColor,
      animationDuration: '650ms',
      zIndex: 9,
      [`& .${circularProgressClasses.circle}`]: {
        strokeLinecap: 'round',
      },
    },
    fixed: {
      position: 'relative',
      color:
        background === 'light'
          ? theme.palette.common.doss.gray[4]
          : theme.palette.common.doss.gray[10],
    },
  }))
